import { QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query'

import { CSRF } from '../utils/CSRF'
import { ObjectUtils } from '../utils/ObjectUtils'
import { recordingsRepository } from '.'
import { BaseRepository } from './BaseRepository'
import { GenericResponse } from './HttpClient/BaseHttpClient'

export class RecordingsRepository extends BaseRepository {
  get(voiceUuid: string, recordingUuid: string) {
    return super.getHttpClientInstance().get<Resemble.API.V2.ReadResponse<Resemble.API.V2.Recording>>(`/api/v2/voices/${voiceUuid}/recordings/${recordingUuid}`, {
      'Content-Type': 'application/json',
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  update(voiceUuid: string, recordingUuid: string, input: { text?: string; active?: boolean }) {
    return super.getHttpClientInstance().patch(
      `/voices/${voiceUuid}/recordings/${recordingUuid}`,
      { ...input, no_turbo: true },
      {
        'Content-Type': 'application/json',
        'X-CSRF-Token': CSRF.getToken(),
      },
    )
  }

  /**
   * Upload a new Recording.
   * @param voiceUUID
   * @param data
   */
  uploadRecordings(voiceUUID: string, data: FormData) {
    return super.getHttpClientInstance().post(`/api/v1/voices/${voiceUUID}/recordings`, data)
  }

  getDenoiseStatus(voiceUuid: string, recordingUuid: string) {
    return super.getHttpClientInstance().get<Resemble.DenoiseStatus>(`/voices/${voiceUuid}/recordings/${recordingUuid}/denoise_status`, {
      'Content-Type': 'application/json',
      'X-CSRF-Token': CSRF.getToken(),
    })
  }

  /**
   * Paginate the recorded clips.
   * @param currentVoice The voice slug to paginate the recordings on.
   * @param data Data object in the shape of PaginateRecsObject.
   */
  paginateVoiceRecordings(currentVoice: string, data: PaginateRecsObject) {
    const querystring = ObjectUtils.convertObjToQueryString(data)

    return super.getHttpClientInstance().get(`/api/v1/voices/${currentVoice}/recordings?${querystring}`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Delete a recorded voice clip.
   * @param clipId The id of the voice clip to delete.
   * @return Promise<GenericResponse> A promise containing the response.
   */
  deleteRecording(voiceId: string, clipId: string): Promise<GenericResponse> {
    return super.getHttpClientInstance().delete(`/api/v1/voices/${voiceId}/recordings/${clipId}`, {
      Accept: 'application/json',
    })
  }
}

interface PaginateRecsObject {
  page: number
  include_audio?: boolean
  search?: string
  hide_inactive?: boolean
  fill?: boolean
}

type PaginatedRecordingsQueryParams = {
  currentVoice: string
  data: PaginateRecsObject
}
export const usePaginatedRecordingsQuery = (
  params: PaginatedRecordingsQueryParams,
  queryOptions?: Omit<UseQueryOptions<{ recordings: Resemble.Recording[]; count: number }, unknown, { recordings: Resemble.Recording[]; count: number }, QueryKey>, 'queryKey' | 'queryFn'> | undefined,
) => {
  const queryKey = [params]
  return useQuery<{ recordings: Resemble.Recording[]; count: number }>(
    queryKey,
    async () => {
      return (await recordingsRepository.paginateVoiceRecordings(params.currentVoice, params.data)).data
    },
    { ...queryOptions },
  )
}

type DeleteRecordingMutationParams = { voice: string; uuid: string }
type DeleteRecordingMutationOptions = Omit<UseMutationOptions<any, unknown, any, unknown>, 'mutationFn'> | undefined
export const useDeleteRecordingMutation = (mutationOptions?: DeleteRecordingMutationOptions) => {
  return useMutation(
    async (params: DeleteRecordingMutationParams): Promise<any> => {
      const { voice, uuid } = params
      const response = await recordingsRepository.deleteRecording(voice, uuid)
      return response.data
    },
    {
      ...mutationOptions,
    },
  )
}

type UpdateRecordingMutationParams = { voiceUuid: string; recordingUuid: string; input: { text?: string; active?: boolean } }
type UpdateRecordingMutationOptions = Omit<UseMutationOptions<any, unknown, any, unknown>, 'mutationFn'> | undefined
export const useUpdateRecordingMutation = (mutationOptions?: UpdateRecordingMutationOptions) => {
  return useMutation(
    async (params: UpdateRecordingMutationParams): Promise<any> => {
      const { voiceUuid, recordingUuid, input } = params
      const response = await recordingsRepository.update(voiceUuid, recordingUuid, input)
      return response.data
    },
    {
      ...mutationOptions,
    },
  )
}
